import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, Lock, ReceiptText, ShieldCheck, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { act, useFetch } from "@/lib/hooks";
import { can, da, fmtDate } from "@/lib/format";
import { Empty, Info, Loading, PageHeader, Panel, PdfButton, Photos, StatusBadge } from "@/components/common";
import { selectCls } from "@/components/forms";
import { WhatsAppButton, WhatsAppHistory } from "@/components/WhatsApp";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const QC_CHECKS = ["Travaux conformes à l'OR", "Niveaux vérifiés", "Serrages contrôlés", "Aucun voyant au tableau de bord", "Véhicule propre"];

function QcPanel({ order, onDone }: { order: any; onDone: () => void }) {
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [road, setRoad] = useState(false);
  const [comment, setComment] = useState("");
  const all = QC_CHECKS.every((c) => checks[c]);
  const submit = async (result: string) => {
    if (await act(() => api.post(`/orders/${order.id}/qc`, { result, checks, comment, road_test: road }), result === "ok" ? "Contrôle qualité validé" : "OR renvoyé à l'atelier")) onDone();
  };
  return (
    <Panel title="Contrôle qualité" testId="qc-panel">
      <div className="space-y-2">
        {QC_CHECKS.map((c) => (
          <button key={c} onClick={() => setChecks({ ...checks, [c]: !checks[c] })} className={`flex h-12 w-full items-center gap-3 rounded-sm border px-3 text-left text-sm transition-colors ${checks[c] ? "border-emerald-500/60 bg-emerald-500/10" : "border-border hover:bg-accent"}`} data-testid={`qc-check-${QC_CHECKS.indexOf(c)}`}>
            <ShieldCheck className={`h-4 w-4 ${checks[c] ? "text-emerald-400" : "text-muted-foreground"}`} />{c}
          </button>
        ))}
        <label className="flex items-center gap-2 py-2 text-sm"><input type="checkbox" checked={road} onChange={(e) => setRoad(e.target.checked)} data-testid="qc-road-test" />Essai routier effectué</label>
        <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire (obligatoire en cas de refus)" data-testid="qc-comment-input" />
        <div className="grid grid-cols-2 gap-2 pt-2">
          <Button variant="outline" className="h-12" disabled={!comment} onClick={() => submit("refuse")} data-testid="qc-refuse-btn"><X className="mr-2 h-4 w-4" />Refuser</Button>
          <Button className="h-12 bg-emerald-600 hover:bg-emerald-700" disabled={!all} onClick={() => submit("ok")} data-testid="qc-validate-btn"><Check className="mr-2 h-4 w-4" />Valider</Button>
        </div>
      </div>
    </Panel>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { data: o, reload } = useFetch<any>(`/orders/${id}`);
  const [mechs, setMechs] = useState<any[]>([]);
  const manage = can(user?.role, "orders_manage");
  useEffect(() => {
    if (manage) api.get("/users/mechanics").then((r) => setMechs(r.data));
  }, [manage]);
  if (!o) return <Loading />;

  const assign = async (mid: string) => {
    if (await act(() => api.post(`/orders/${o.id}/assign`, { mechanic_id: mid }), "Mécanicien affecté")) reload();
  };
  const close = async () => {
    if (await act(() => api.post(`/orders/${o.id}/close`), "OR clôturé")) reload();
  };
  const invoice = async () => {
    const r = await act(() => api.post("/invoices/from-order", { order_id: o.id }), "Facture brouillon créée");
    if (r) nav(`/factures/${r.data.id}`);
  };

  return (
    <div>
      <PageHeader eyebrow={`Ordre de réparation · devis ${o.quote_number}`} title={o.number}
        actions={<>
          <PdfButton kind="order" id={o.id} label="PDF OR" />
          {o.status === "cloture" && <PdfButton kind="service" id={o.id} label="Bon de service fait" testId="pdf-service-btn" />}
          {o.status === "cloture" && <WhatsAppButton kind="service" id={o.id} label="Service fait par WhatsApp" onSent={reload} testId="service-whatsapp-btn" />}
          {manage && o.status === "qc_ok" && <Button onClick={close} data-testid="close-order-btn"><Lock className="mr-2 h-4 w-4" />Clôturer l'OR</Button>}
          {can(user?.role, "billing") && o.status === "cloture" && !o.invoice && <Button onClick={invoice} data-testid="create-invoice-btn"><ReceiptText className="mr-2 h-4 w-4" />Facturer</Button>}
          {o.invoice && <Link to={`/factures/${o.invoice.id}`}><Button variant="outline" data-testid="order-invoice-link">Facture {o.invoice.number || "brouillon"}</Button></Link>}
        </>}>
        <div className="mt-3 flex flex-wrap items-center gap-3"><StatusBadge status={o.status} testId="order-status" /><span className="text-sm text-muted-foreground"><span className="font-mono-data">{o.plate}</span> · {o.vehicle_label} · {o.client_name}</span></div>
      </PageHeader>
      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <Panel title="Affectation & consignes">
            <div className="grid gap-5 md:grid-cols-3">
              <div>
                <div className="eyebrow mb-1.5">Mécanicien</div>
                {manage && o.status !== "cloture" ? (
                  <select className={selectCls} value={o.mechanic_id || ""} onChange={(e) => assign(e.target.value)} data-testid="assign-mechanic-select">
                    <option value="" disabled>Choisir…</option>
                    {mechs.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                ) : <div className="text-sm">{o.mechanic_name || "—"}</div>}
              </div>
              <Info label="Temps passé" value={`${Math.floor(o.total_minutes / 60)} h ${o.total_minutes % 60} min`} testId="order-time" />
              <Info label="Montant devis TTC" value={da(o.quote_totals?.ttc)} />
            </div>
            <div className="mt-5"><Info label="Consignes" value={o.instructions} /></div>
            <WhatsAppHistory entries={o.whatsapp} testId="service-whatsapp-history" />
          </Panel>
          <Panel title="Travaux">
            {o.tasks.map((t: any, i: number) => (
              <div key={i} className="flex items-center justify-between border-b border-border py-2.5 text-sm last:border-0">
                <span className={t.done ? "" : "text-muted-foreground"}>{t.label}</span>
                {t.done ? <span className="text-xs text-emerald-400">Fait · {t.by}</span> : <span className="text-xs text-muted-foreground">À faire</span>}
              </div>
            ))}
          </Panel>
          <Panel title="Pièces">
            <div className="grid gap-6 md:grid-cols-2">
              <div><div className="eyebrow mb-2">Prévues au devis</div>{o.parts_planned.length ? o.parts_planned.map((p: any, i: number) => <div key={i} className="py-1 text-sm">{p.qty} × {p.label}</div>) : <Empty text="Aucune" />}</div>
              <div><div className="eyebrow mb-2">Sorties du stock</div>{o.parts_used.length ? o.parts_used.map((p: any) => <div key={p.id} className="py-1 text-sm">{p.qty} × {p.label} <span className="text-xs text-muted-foreground">· {p.by}</span></div>) : <Empty text="Aucune" />}</div>
            </div>
          </Panel>
          <Panel title={`Photos atelier (${o.photos.length})`}><Photos entity="orders" entityId={o.id} photos={o.photos} onAdded={reload} canUpload={manage && o.status !== "cloture"} /></Panel>
        </div>
        <div className="space-y-6">
          {manage && o.status === "termine" && <QcPanel order={o} onDone={reload} />}
          {o.qc && (
            <Panel title="Dernier contrôle qualité">
              <StatusBadge status={o.qc.result === "ok" ? "qc_ok" : "refuse"} />
              <p className="mt-2 text-sm">{o.qc.comment || "—"}</p>
              <p className="text-xs text-muted-foreground">{o.qc.by} · {fmtDate(o.qc.at)}{o.qc.road_test ? " · essai routier" : ""}</p>
            </Panel>
          )}
          <Panel title="Pointages">
            {o.time_logs.length === 0 ? <Empty text="Aucun pointage" /> : o.time_logs.map((t: any) => (
              <div key={t.id} className="flex justify-between border-b border-border py-2 text-xs last:border-0"><span>{t.user_name} · {fmtDate(t.start)}</span><span className="font-mono-data">{t.minutes ? `${t.minutes} min` : "en cours"}</span></div>
            ))}
          </Panel>
          <Panel title="Notes atelier">
            {o.notes.length === 0 ? <Empty text="Aucune note" /> : o.notes.map((n: any, i: number) => (
              <div key={i} className="border-l-2 border-border py-1.5 pl-3 text-sm"><div>{n.text}</div><div className="text-xs text-muted-foreground">{n.by} · {fmtDate(n.at)}</div></div>
            ))}
          </Panel>
        </div>
      </div>
    </div>
  );
}
